import './styles/reset.css'
import './styles/tokens.css'
import './styles/app.css'

import { PANELS, type PanelId } from './config/church'
import {
  BUILD_ID,
  IDLE_TIMEOUT_MS,
  SELF_RELOAD_AFTER_MS,
  SELF_RELOAD_CHECK_MS,
  SELF_RELOAD_HOUR_NY,
} from './config/kiosk'
import { installDiagnostics } from './core/diagnostics'
import * as fully from './core/fully'
import { IdleTimer } from './core/idle'
import { Router } from './core/router'
import { SlideStore } from './core/slides'
import { mountStage } from './core/stage'
import { createBulletinScreen } from './screens/bulletin'
import { createDirectionsScreen } from './screens/directions'
import { createHomeScreen } from './screens/home'
import { createNewsScreen } from './screens/news'
import { Slideshow } from './screens/slideshow'
import { createWorshipScreen } from './screens/worship'

const stage = document.getElementById('stage')
const screens = document.getElementById('screens')

if (!stage || !screens) {
  throw new Error('index.html is missing #stage / #screens')
}

mountStage(stage)

const bootedAt = Date.now()

const params = new URLSearchParams(window.location.search)

/** Each of the three panels gets its own Start URL: `...?panel=lobby`. */
function currentPanel(): PanelId | undefined {
  const value = params.get('panel')
  return value && value in PANELS ? (value as PanelId) : undefined
}

const panel = currentPanel()

/**
 * `slideshow` (default): the pictures editors publish on the church website, and
 * nothing else — the touch menu waits until its content is decided.
 * `hub` (`?mode=hub`): the touch menu, for trying it out on one panel.
 */
const mode = params.get('mode') === 'hub' ? 'hub' : 'slideshow'

if (mode === 'hub') startHub()
else startSlideshow()

document.body.classList.remove('is-booting')

/* ---------------------------------------------------------------- slideshow */

function startSlideshow(): void {
  const root = document.createElement('div')
  root.className = 'screen is-active'
  screens!.appendChild(root)

  const slideshow = new Slideshow(root)
  const store = new SlideStore(
    panel,
    () => slideshow.displayed(),
    (slides) => slideshow.setSlides(slides),
  )

  const saved = store.loadSaved()
  if (saved) slideshow.setSlides(saved)
  else slideshow.setSlides([])
  store.start()
  if (import.meta.env.DEV) {
    // For poking at it from the console in `npm run dev`; not in the built app.
    ;(window as unknown as Record<string, unknown>).__kiosk = { store, slideshow }
  }

  installDiagnostics(stage!, async () => ({
    mode,
    panel: panel ?? '-',
    build: BUILD_ID,
    uptime: formatDuration(Date.now() - bootedAt),
    slides: String(store.status.count),
    lastSuccess: store.status.lastSuccessAt ?? '-',
    lastFailure: store.status.lastFailureAt ? `${store.status.lastFailureAt} — ${store.status.lastFailure ?? ''}` : '-',
    generatedAt: store.status.generatedAt ?? '-',
    cachedPictures: await cachedPictureCount(),
  }))

  reloadForNewBuild(() => slideshow.isInteracting())
  reloadDaily(() => slideshow.isInteracting())
}

/* ---------------------------------------------------------------------- hub */

function startHub(): void {
  installDiagnostics(stage!, async () => ({ mode, panel: panel ?? '-', build: BUILD_ID }))

  const router = new Router(screens!, {
    home: createHomeScreen,
    worship: createWorshipScreen,
    news: createNewsScreen,
    bulletin: createBulletinScreen,
    directions: createDirectionsScreen,
  })

  const goHome = (): void => router.go('home', panel)

  // Return to the hub after a while with no touch.
  const idle = new IdleTimer(IDLE_TIMEOUT_MS, goHome)
  router.subscribe((id) => {
    if (id === 'home') idle.pause()
    else idle.resume()
  })
  idle.start()
  goHome()

  if (fully.isFully()) {
    document.documentElement.dataset.runtime = 'fully'
    fully.on('onScreensaverStart', goHome)
    fully.on('screenOn', goHome)
  }
}

/* ------------------------------------------------------------------ reloads */

/**
 * A new build reaches the panel as a new service worker, which takes over at once
 * (skipWaiting + clientsClaim) — but the page keeps running the old code until it
 * loads again. Reload once when that happens. Not on the very first install, when
 * there was no worker before and the page is already the new code.
 */
function reloadForNewBuild(isBusy: () => boolean): void {
  if (!('serviceWorker' in navigator)) return
  const hadController = Boolean(navigator.serviceWorker.controller)
  let reloading = false
  const reload = (): void => {
    if (reloading) return
    if (isBusy()) {
      window.setTimeout(reload, 2_000)
      return
    }
    reloading = true
    window.location.reload()
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) reload()
  })
}

/**
 * Once a day, at 4 a.m. New York time and only when online, reload — so the panel
 * picks up a new build even on a board where Fully cannot turn the screen off (and
 * so never reloads on screen-on).
 */
function reloadDaily(isBusy: () => boolean): void {
  window.setInterval(() => {
    if (Date.now() - bootedAt < SELF_RELOAD_AFTER_MS) return
    if (!navigator.onLine || isBusy()) return
    if (newYorkHour() !== SELF_RELOAD_HOUR_NY) return
    window.location.reload()
  }, SELF_RELOAD_CHECK_MS)
}

function newYorkHour(): number {
  const text = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }).format(new Date())
  // Older engines write midnight as "24".
  return parseInt(text, 10) % 24
}

/* -------------------------------------------------------------- diagnostics */

async function cachedPictureCount(): Promise<string> {
  try {
    if (!('caches' in window)) return '-'
    const cache = await caches.open('kiosk-slides')
    return String((await cache.keys()).length)
  } catch {
    return '-'
  }
}

function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60_000)
  const hours = Math.floor(minutes / 60)
  return hours > 0 ? `${hours}시간 ${minutes % 60}분` : `${minutes}분`
}
